/**
 * The Products page's ONE drawer (owner ruling N-54, 2026-09-17): "Row → right-side
 * drawer holding every edit the row card has today; Change price lives in the drawer …
 * Add product = toolbar button opening the same drawer."
 *
 * Three views of one `Drawer`, so there is never more than one dialog on the page:
 *   · EDIT — the former `ManageProductModal`, control for control: name behind the
 *     three-edges confirm, classification select for `grp:true` only with THE ONE HOME of
 *     the 40-word explanation beside it (v22-C fault 1, N-54 item 3: once, as help text,
 *     never per row), shelf select in the single-country view (`null` clears), term
 *     checkbox, confirm-the-name DISABLED WITH THE REASON while `unclassified`,
 *     retire/reinstate — plus the price line the card used to carry and the button that
 *     opens…
 *   · PRICE — the former "Set a price" modal, INSIDE the drawer rather than stacked on it
 *     (two open dialogs are two focus traps): price/currency/date, the T-P2 preview
 *     sentences, the clear-the-flag checkbox that only ever clears.
 *   · CREATE — the former create modal: the title names the country the create lands
 *     in, the immutability paragraph is read at decision time, `kind` and
 *     `verify_before_prod` are never POSTed, a 409 renders the server's sentence here.
 *
 * Every rule is the predecessor's verbatim and stays pinned by `products-page.test.tsx`;
 * only the address changed. The rename confirm still REPLACES the drawer (the page closes
 * it before opening `ConfirmDialog`), for the same one-dialog reason.
 */

import { Alert, Button, Checkbox, Chip, Drawer, SelectField, TextField } from '../../components/ui';
import type { CountryCode } from '../../domain/countries';
import { PRODUCT_KINDS, PRODUCT_KIND_LABEL } from './catalogue-api';
import type { CountryProductDto, ProductDto, ProductKind, UpdateProductBody } from './catalogue-api';
import { priceText } from './products-model';
import type { ProductHistory } from './products-model';

export interface DraftPrice {
  productId: string;
  productCode: string;
  /** The COUNTRY whose price list the PUT targets — carried on the draft because the
   * group-admin view interleaves six countries' rows. */
  cc: CountryCode;
  price: string;
  /**
   * WHICH LIST (migration 66) — fixed by the button that opened the form ("Change CDF price",
   * "Add a USD price"): every entry point knows its list, so the form never asks and a price
   * can never land in a list the operator did not mean.
   */
  currency: string;
  effectiveFrom: string;
  clearFlag: boolean;
  /**
   * Leave this list's price BLANK — priced per customer, typed on every quote (owner ruling
   * R3, 2026-09-22: *"prices are different per customer so we got to keep the price bracket
   * blank"*). Sent as `price: null`; the Price field is then off.
   */
  perCustomer: boolean;
}

export interface CreateDraft {
  name: string;
  code: string;
  shelf: string;
  term: boolean;
}

export type DrawerView =
  | { kind: 'create' }
  | {
      kind: 'edit';
      product: ProductDto;
      /** Every list this product has in `cc` — one per currency (migration 66); empty = none. */
      lists: readonly ProductHistory[];
      /** The currencies `cc` quotes in, home first — one block per currency. */
      currencies: readonly string[];
      cc: CountryCode;
    };

export function ProductDrawer({
  view,
  workCc,
  shelfOptions,
  hasShelves,
  canClassify,
  busy,
  actionError,
  nameEdit,
  onNameEdit,
  onNameSave,
  onPatch,
  priceDraft,
  previewSentences,
  onBeginPrice,
  onPriceDraft,
  onPriceSave,
  onPriceCancel,
  createDraft,
  onCreateDraft,
  onCreateSubmit,
  onClose,
}: {
  view: DrawerView;
  /** The country a create lands in — never `'ALL'`. */
  workCc: CountryCode;
  /** `null` in the interleaved ALL view, where no re-filing control exists. */
  shelfOptions: { value: string; label: string }[] | null;
  /** Whether the country in view has ANY shelf row, retired ones included — the create
   * view shows its Shelf select on that (the predecessor's condition), not on active ones. */
  hasShelves: boolean;
  /** `grp:true` — the ONE seat the server lets classify a product. */
  canClassify: boolean;
  busy: boolean;
  actionError: string | null;
  /** The in-progress edit value, or `null` when the name is not being renamed. */
  nameEdit: string | null;
  onNameEdit: (value: string | null) => void;
  onNameSave: (next: string) => void;
  onPatch: (body: UpdateProductBody) => void;
  /** Non-null while the PRICE view is showing for the edited product. */
  priceDraft: DraftPrice | null;
  previewSentences: readonly string[];
  onBeginPrice: (from: CountryProductDto | null, currency: string) => void;
  onPriceDraft: (next: DraftPrice) => void;
  onPriceSave: () => void;
  onPriceCancel: () => void;
  createDraft: CreateDraft;
  onCreateDraft: (next: CreateDraft) => void;
  onCreateSubmit: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const errorBlock =
    actionError === null ? null : (
      <Alert tone="danger" title="That did not save">
        <p>{actionError}</p>
      </Alert>
    );

  /* ── CREATE ───────────────────────────────────────────────────────────────── */
  if (view.kind === 'create') {
    return (
      <Drawer open title={`Add a product in ${workCc}`} busy={busy} onClose={onClose}>
        <form
          id="create-product-form"
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (createDraft.name.trim() === '' || createDraft.code.trim() === '') return;
            onCreateSubmit();
          }}
        >
          {errorBlock}
          <TextField
            label="Name"
            value={createDraft.name}
            onChange={(event) => {
              onCreateDraft({ ...createDraft, name: event.target.value });
            }}
            placeholder="TallyPrime Silver"
          />
          <TextField
            label="Code"
            value={createDraft.code}
            onChange={(event) => {
              onCreateDraft({ ...createDraft, code: event.target.value });
            }}
            placeholder="TP-SILVER"
          />
          {shelfOptions !== null && hasShelves ? (
            <SelectField
              label="Shelf"
              id="create-product-shelf"
              options={shelfOptions}
              value={createDraft.shelf}
              onChange={(value) => {
                onCreateDraft({ ...createDraft, shelf: value });
              }}
            />
          ) : null}
          <Checkbox
            label="Sold on a term"
            checked={createDraft.term}
            onChange={(event) => {
              onCreateDraft({ ...createDraft, term: event.target.checked });
            }}
          />
          {/* The two immutabilities and the safety net, stated where the decision is
            * being made rather than discovered as a refusal later (§2.3). */}
          <p className="text-label text-fg-secondary">
            The code is permanent, and the product stays in {workCc} — a mistyped code is
            retired and re-created so documents that cite it stay intact. The name arrives
            unconfirmed: it stays off quotes and customer documents until someone confirms
            it, deliberately a second step.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="quiet" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || createDraft.name.trim() === '' || createDraft.code.trim() === ''}
            >
              {/* 'Create', not 'Add' — the toolbar button that OPENED this drawer says
                * 'Add product', and two controls sharing one accessible name would make
                * the pair unaddressable. */}
              Create product
            </Button>
          </div>
        </form>
      </Drawer>
    );
  }

  const { product, lists, currencies, cc } = view;
  /** More than one list is possible here — label every control with its currency. */
  const twoLists = currencies.length > 1;

  /* ── PRICE — inside the same dialog ──────────────────────────────────────── */
  if (priceDraft !== null && priceDraft.productId === product.id) {
    return (
      <Drawer
        open
        title={`Edit ${product.code}`}
        busy={busy}
        onClose={onClose}
        footer={
          <>
            <Button variant="quiet" onClick={onPriceCancel}>
              Cancel
            </Button>
            <Button
              onClick={onPriceSave}
              disabled={
                (!priceDraft.perCustomer && priceDraft.price.trim() === '') ||
                priceDraft.currency.trim() === '' ||
                busy
              }
            >
              Save price
            </Button>
          </>
        }
      >
        {/* The former modal's title, kept as the section's heading so the dialog still
          * says which product and which country's price list this write targets. */}
        <h3 className="text-body font-medium text-fg">
          Set a price — {priceDraft.productCode} in {priceDraft.cc}
          {twoLists ? `, ${priceDraft.currency} list` : ''}
        </h3>
        {errorBlock}
        <div className="flex flex-wrap items-start gap-3">
          <div className="w-40">
            <TextField
              label="Price"
              value={priceDraft.perCustomer ? '' : priceDraft.price}
              disabled={priceDraft.perCustomer}
              placeholder={priceDraft.perCustomer ? 'Typed on each quote' : undefined}
              onChange={(event) => {
                onPriceDraft({ ...priceDraft, price: event.target.value });
              }}
            />
          </div>
          <div className="w-28">
            {/* READ-ONLY: the button that opened this form chose the list (migration 66). */}
            <TextField label="Currency" value={priceDraft.currency} readOnly />
          </div>
          <div className="w-44">
            <TextField
              label="Applies from"
              type="date"
              value={priceDraft.effectiveFrom}
              onChange={(event) => {
                onPriceDraft({ ...priceDraft, effectiveFrom: event.target.value });
              }}
            />
          </div>
        </div>
        <Checkbox
          label="Priced per customer — leave the price blank; it is typed on each quote"
          checked={priceDraft.perCustomer}
          onChange={(event) => {
            onPriceDraft({ ...priceDraft, perCustomer: event.target.checked });
          }}
        />
        <p className="text-label text-fg-secondary">
          Leave the date blank for today. A future date is queued and does not change what
          is quoted now; a past date changes what applied then.
          {twoLists
            ? ` This is the ${priceDraft.currency} list only — the other currency's price is separate, and prices are never converted between them.`
            : ''}
        </p>
        {previewSentences.length === 0 ? null : (
          /*
           * WHAT WILL HAPPEN, before it does — the queued-rise case is the one this
           * exists for: an operator typing today's correction must SEE that a rise
           * already queued for next quarter still takes over.
           */
          <ul className="space-y-1" aria-label="What this change will do">
            {previewSentences.map((sentence) => (
              <li key={sentence} className="text-label text-fg">
                {sentence}
              </li>
            ))}
          </ul>
        )}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={priceDraft.clearFlag}
            onChange={(event) => {
              onPriceDraft({ ...priceDraft, clearFlag: event.target.checked });
            }}
          />
          <span className="text-body text-fg">This price is reviewed — clear the flag</span>
        </label>
      </Drawer>
    );
  }

  /* ── EDIT ─────────────────────────────────────────────────────────────────── */
  return (
    <Drawer
      open
      title={`Edit ${product.code}`}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          {product.verify_before_prod ? (
            /*
             * ⚠ DISABLED WITH A REASON, NOT HIDDEN — the shelf Delete button's pattern,
             * for the same reason. The seat MAY confirm the name; it may not confirm one
             * on a product nobody has classified, because the server refuses that
             * (`assertClassifiedBeforeProd`, ruling Q4 iii). The refusal is the truth
             * either way and renders verbatim if it arrives; this only stops the button
             * from being one that can currently do nothing but 422.
             */
            <Button
              variant="quiet"
              disabled={busy || product.kind === 'unclassified'}
              title={
                product.kind === 'unclassified'
                  ? 'A group administrator has to classify this product first — until then Angola and DR Congo cannot tell whether selling it requires the government patch.'
                  : undefined
              }
              onClick={() => {
                onPatch({ verify_before_prod: false });
              }}
            >
              Confirm the name
            </Button>
          ) : null}
          {/* Retire/reinstate — reversible, so no dialog: the status cell and the
            * button both say which state the row is in, and the server remains the
            * truth on refusal. */}
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() => {
              onPatch({ active: !product.active });
            }}
          >
            {product.active ? 'Retire' : 'Reinstate'}
          </Button>
        </>
      }
    >
      {errorBlock}

      <div className="flex flex-wrap gap-2">
        <Chip>{PRODUCT_KIND_LABEL[product.kind as ProductKind] ?? product.kind}</Chip>
        {product.verify_before_prod ? <Chip>Name not confirmed</Chip> : null}
        {product.active ? null : <Chip>Withdrawn</Chip>}
      </div>

      {nameEdit === null ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 truncate text-body text-fg" title={product.name}>
            {product.name}
          </p>
          <Button
            variant="quiet"
            disabled={busy}
            onClick={() => {
              onNameEdit(product.name);
            }}
          >
            Edit name
          </Button>
        </div>
      ) : (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const next = nameEdit.trim();
            if (next === '' || next === product.name) {
              onNameEdit(null);
              return;
            }
            onNameSave(next);
          }}
        >
          <div className="min-w-48 grow">
            <TextField
              label={`New name for ${product.code}`}
              value={nameEdit}
              onChange={(event) => {
                onNameEdit(event.target.value);
              }}
            />
          </div>
          <Button type="submit" disabled={busy}>
            Save name
          </Button>
          <Button
            type="button"
            variant="quiet"
            onClick={() => {
              onNameEdit(null);
            }}
          >
            Cancel
          </Button>
        </form>
      )}

      {/*
        * ⚠ THE CLASSIFICATION CONTROL — `grp:true` ONLY, and the condition is the whole
        * point (D-3, ruling Q4 iii). It is not a security boundary: the server refuses a
        * Country Admin with `PERMISSION_DENIED` whatever this renders. It is rendered
        * conditionally because a control a Country Admin can SEE and cannot USE teaches
        * that seat classification is theirs and then answers 403 — a worse screen than
        * no control at all. The chip above stays for every seat, so nobody loses the
        * ability to READ what a product is.
        */}
      {canClassify ? (
        <div>
          <SelectField
            label="Classification"
            id={`kind-${product.id}`}
            options={PRODUCT_KINDS.map((value) => ({ value, label: PRODUCT_KIND_LABEL[value] }))}
            value={product.kind}
            onChange={(value) => {
              // Guarded, not because the select can produce anything else, but because
              // a no-op PATCH would emit a `product.updated` event for a change nobody
              // made — the service returns early on an equal value, and this saves the
              // round trip that discovers that.
              if (value !== product.kind) onPatch({ kind: value as ProductKind });
            }}
          />
          {/* THE ONE HOME of the explanation (v22-C fault 1; N-54 item 3 — once, as help
            * text beside the decision it explains, never per row). */}
          <p className="mt-1 text-label text-fg-secondary">
            What this product IS — it decides whether Angola and DR Congo may sell it
            without the government patch. A product stays off quotes until it is
            classified and its name confirmed, in that order.
          </p>
        </div>
      ) : null}

      {shelfOptions === null ? null : (
        <SelectField
          label="Shelf"
          id={`shelf-${product.id}`}
          options={shelfOptions}
          value={product.product_group_id ?? ''}
          onChange={(value) => {
            // `null`, NOT the empty string: the API reads `!== undefined` and `''`
            // is not a group id — this is where "clear it" is actually formed.
            onPatch({ product_group_id: value === '' ? null : value });
          }}
        />
      )}

      <Checkbox
        label="Sold on a term"
        checked={product.term_bearing}
        disabled={busy}
        onChange={(event) => {
          onPatch({ term_bearing: event.target.checked });
        }}
      />

      {/* ── the price in this country: the three states, then the versions ── */}
      <section className="space-y-2 border-t border-line pt-4" aria-labelledby={`price-${product.id}`}>
        <h3 id={`price-${product.id}`} className="text-th uppercase text-fg">
          Price in {cc}
        </h3>
        {lists.length === 0 ? (
          <p className="text-label text-fg-secondary">
            No price row — this country cannot quote it.
          </p>
        ) : null}
        {/*
          * ONE BLOCK PER LIST (migration 66, 2026-09-22). A country quoting in one currency
          * shows exactly the block it always did. Congo shows a CDF list and a USD list, each
          * with its own price, versions, queue and button — and "Add a USD price" where the
          * USD list is empty. Nothing is ever converted between them.
          */}
        {(twoLists ? currencies : [currencies[0] ?? lists[0]?.currency ?? '']).map((currency) => {
          const history =
            (twoLists
              ? lists.find((list) => list.currency === currency)
              : (lists.find((list) => list.currency === currency) ?? lists[0])) ?? null;
          const current = history?.current ?? null;
          return (
            <div key={currency} className="space-y-2" data-testid={`price-list-${currency}`}>
              {twoLists ? (
                <h4 className="text-label font-medium text-fg">{currency} list</h4>
              ) : null}
              {history === null ? (
                twoLists && lists.length > 0 ? (
                  <p className="text-label text-fg-secondary">
                    No {currency} price — not offered on {currency} quotes.
                  </p>
                ) : null
              ) : current === null ? (
                <p className="text-body text-fg">No price applies today</p>
              ) : (
                <p className="text-body text-fg tnum">
                  {priceText(current)}
                  <span className="text-label text-fg-secondary">
                    {' '}
                    from {current.effective_from}
                    {current.effective_to === null ? '' : ` to ${current.effective_to}`}
                  </span>
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {current !== null && !current.active ? <Chip>Withdrawn in this country</Chip> : null}
                {current?.verify_before_prod === true ? <Chip>Price not confirmed</Chip> : null}
                {history !== null && history.future.length > 0 ? (
                  <Chip>
                    {history.future.length === 1
                      ? `A change is queued from ${history.future[0]?.effective_from ?? ''}`
                      : `${String(history.future.length)} changes queued`}
                  </Chip>
                ) : null}
              </div>
              {history !== null && history.versions.length > 1 ? (
                <details>
                  <summary className="cursor-pointer text-label text-fg-secondary">
                    {history.versions.length} versions
                  </summary>
                  <ul className="mt-1 space-y-1">
                    {history.versions.map((row) => (
                      <li
                        key={`${row.product_id}-${row.currency}-${row.effective_from}`}
                        className="text-label text-fg-secondary"
                      >
                        {row.effective_from} – {row.effective_to ?? 'open'} · {priceText(row)} ·{' '}
                        {row.window_state}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              <div>
                <Button
                  variant="quiet"
                  disabled={busy || currency === ''}
                  onClick={() => {
                    onBeginPrice(current, currency);
                  }}
                >
                  {history === null
                    ? twoLists
                      ? `Add a ${currency} price`
                      : 'Add a price'
                    : twoLists
                      ? `Change ${currency} price`
                      : 'Change price'}
                </Button>
              </div>
            </div>
          );
        })}
      </section>
    </Drawer>
  );
}
